import {useMemo} from 'react';
import {BoundSearchQuery} from "../../types/common/BoundSearchQuery";
import {useReactiveGetRequest} from "./useReactiveGetRequest";
import useDebounce from "../useDebounce";

interface UseGetBoundReportSummaryProps {
  requestUrl: string;
  requestParams?: BoundSearchQuery;
}

const extractKeyParams = (params: BoundSearchQuery | undefined) => {
  if (!params) return undefined;
  // we want to ignore pagination and sorting params for the summary request, so we omit
  // them from the stringification that determines when to update the queryParams
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { page, size, sortBy, sortDir, ...rest } = params;
  return rest;
};

/**
 * Hook for reactively grabbing data from the supplied endpoint.
 * Automatically aborts previous requests and separates pagination info and the data
 **/
const useGetBoundReportSummary = <T>({
  requestUrl,
  requestParams
}: UseGetBoundReportSummaryProps) => {
  const keyParams = extractKeyParams(requestParams);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const queryParams = useMemo(() => (keyParams), [JSON.stringify(keyParams)]);

  const debouncedQueryParams = useDebounce(queryParams, 250);

  const {
    data: reportSummary,
    isLoading: isReportSummaryLoading,
    error: reportSummaryError,
  } = useReactiveGetRequest<T>({ requestUrl, queryParams: debouncedQueryParams});

  return {
    reportSummary,
    isReportSummaryLoading,
    reportSummaryError
  }
}

export { useGetBoundReportSummary };